<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="800px"
    persistent
    content-class="clone-asset-dialog"
  >
    <v-card class="clone-asset-dialog__card">
      <v-card-title class="clone-asset-dialog__header">
        <div class="clone-asset-dialog__header-title white--text pl-4">
          Clone Asset
        </div>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text
        class="clone-asset-dialog__content clone-asset-dialog__scrollable-content"
      >
        <div>
          <!-- Error Message Alert -->
          <v-alert
            v-if="errorMessage"
            type="error"
            dismissible
            @input="errorMessage = ''"
            class="mb-4"
          >
            {{ errorMessage }}
          </v-alert>

          <!-- Clone Description -->
          <div class="clone-asset-dialog__description">
            <v-icon left color="info" size="20">mdi-information</v-icon>
            <span class="clone-asset-dialog__description-text">
              This will create a copy of the current asset in your market. You
              can customize which attributes and content to include in the clone
              below.
            </span>
          </div>

          <!-- Asset ID -->
          <div class="clone-asset-dialog__section">
            <v-text-field
              v-model="cloneData.identifier"
              label="Asset ID"
              outlined
              hide-details
            ></v-text-field>
          </div>

          <!-- Asset Name -->
          <div class="clone-asset-dialog__section">
            <v-text-field
              v-model="cloneData.name"
              label="Asset Name"
              outlined
              hide-details
            ></v-text-field>
          </div>

          <!-- Editable Description -->
          <div class="clone-asset-dialog__section mt-4">
            <v-textarea
              v-model="cloneData.description"
              label="Asset Description"
              outlined
              rows="3"
              hide-details
            ></v-textarea>
          </div>

          <!-- Target Market Selection -->
          <div class="clone-asset-dialog__section mt-4">
            <v-select
              v-model="selectedTargetMarket"
              :items="availableTargetMarkets"
              item-text="name"
              item-value="id"
              label="Clone to Market"
              outlined
              hide-details
              prepend-inner-icon="mdi-earth"
              placeholder="Select target market"
            >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      No markets available for cloning
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>

            <!-- Note: Since clone button is now hidden when no markets available,
                 this warning message is no longer needed -->
          </div>

          <!-- Asset Attributes -->
          <div
            v-if="cloneData.attributes && cloneData.attributes.length > 0"
            class="clone-asset-dialog__section"
          >
            <v-expansion-panels
              v-model="attributesPanel"
              class="clone-asset-dialog__expansion-panel"
            >
              <v-expansion-panel>
                <v-expansion-panel-header
                  class="clone-asset-dialog__expansion-panel-header"
                >
                  <div
                    class="clone-asset-dialog__expansion-panel-header-content"
                  >
                    <span
                      class="clone-asset-dialog__expansion-panel-header-text"
                    >
                      Attributes ({{ cloneData.attributes.length }})
                    </span>
                    <v-checkbox
                      :input-value="allAttributesSelected"
                      @change="toggleAllAttributes"
                      @click.stop
                      color="primary"
                      hide-details
                      dense
                      class="ma-0 pa-0 clone-asset-dialog__select-all-checkbox"
                      :label="
                        allAttributesSelected ? 'Uncheck All' : 'Select All'
                      "
                    ></v-checkbox>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content
                  class="clone-asset-dialog__expansion-panel-content"
                >
                  <!-- Attributes Helper Message -->
                  <div
                    class="clone-asset-dialog__helper-message clone-asset-dialog__helper-message--spaced"
                  >
                    <v-icon left color="info" size="16"
                      >mdi-lightbulb-outline</v-icon
                    >
                    <span class="clone-asset-dialog__helper-text">
                      We have pre-selected the attributes found in your market.
                      Feel free to uncheck any you don't need, or add any that
                      are missing.
                    </span>
                  </div>

                  <div
                    class="clone-asset-dialog__list clone-asset-dialog__list--columns"
                  >
                    <div
                      v-for="(attribute, index) in cloneData.attributes"
                      :key="index"
                      class="clone-asset-dialog__list-item clone-asset-dialog__list-item--column"
                    >
                      <div class="clone-asset-dialog__list-row">
                        <div class="clone-asset-dialog__list-action">
                          <v-checkbox
                            v-model="attribute.selected"
                            color="primary"
                            hide-details
                            dense
                            class="ma-0 pa-0"
                          ></v-checkbox>
                        </div>
                        <div class="clone-asset-dialog__list-content">
                          <div class="clone-asset-dialog__list-label">
                            {{ attribute.name }}
                          </div>
                          <div class="clone-asset-dialog__list-value">
                            {{ getAttributeDisplayValue(attribute) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <v-card-actions
        class="clone-asset-dialog__actions clone-asset-dialog__footer"
      >
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="closeDialog"
          class="clone-asset-dialog__btn clone-asset-dialog__btn--cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="cloneAsset"
          :disabled="!selectedTargetMarket || isCloning"
          :loading="isCloning"
          class="clone-asset-dialog__btn clone-asset-dialog__btn--clone"
        >
          <v-icon left>mdi-export</v-icon>
          {{ isCloning ? "Cloning..." : cloneButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  name: "CloneAssetDialog",
  components: {},
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    assetId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      cloneData: {
        identifier: "",
        name: "",
        categoryName: "",
        description: "",
        attributes: [],
      },
      attributesPanel: null,
      selectedTargetMarket: null,
      isCloning: false,
      errorMessage: "",
    };
  },
  computed: {
    ...mapState("assetxpstate", ["asset", "markets", "section"]),
    ...mapState("authentication", ["subid"]),
    ...mapGetters("authentication", ["availableTargetMarkets"]),
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    selectedTargetMarketName() {
      if (!this.selectedTargetMarket || !this.markets) {
        return "";
      }
      const market = this.markets.find(
        (m) => m.id === this.selectedTargetMarket
      );
      return market ? market.name : "";
    },
    cloneButtonText() {
      if (!this.selectedTargetMarket) {
        return "Select target market";
      }
      return `Clone to ${this.selectedTargetMarketName}`;
    },
    totalSelectedResources() {
      if (
        !this.cloneData.attributes ||
        !Array.isArray(this.cloneData.attributes)
      ) {
        return 0;
      }
      const selectedAttributes = this.cloneData.attributes.filter(
        (attr) => attr.selected
      ).length;
      return selectedAttributes;
    },
    allAttributesSelected() {
      if (
        !this.cloneData.attributes ||
        this.cloneData.attributes.length === 0
      ) {
        return false;
      }
      return this.cloneData.attributes.every((attr) => attr.selected);
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadAssetData();
        this.selectedTargetMarket = null; // Reset target market selection
        this.errorMessage = ""; // Clear any previous error messages
      }
    },
    availableTargetMarkets(newVal) {
      // Auto-select if only one market is available
      if (newVal && newVal.length === 1 && !this.selectedTargetMarket) {
        this.selectedTargetMarket = newVal[0].id;
      } else if (
        this.selectedTargetMarket &&
        newVal &&
        !newVal.find((market) => market.id === this.selectedTargetMarket)
      ) {
        // Reset if current selection is no longer available
        this.selectedTargetMarket = null;
      }
    },
    "cloneData.identifier"() {
      // Clear error message when user modifies the asset ID
      if (this.errorMessage) {
        this.errorMessage = "";
      }
    },
    "cloneData.name"() {
      // Clear error message when user modifies the asset name
      if (this.errorMessage) {
        this.errorMessage = "";
      }
    },
  },
  methods: {
    ...mapActions("assetxpstate", [
      "GetAsset",
      "CloneAssetToMarket",
      "GetCloneAttributes",
    ]),

    async loadAssetData() {
      // Load basic asset data from store
      if (this.asset && this.asset.assetDetailid === this.assetId) {
        this.cloneData = {
          identifier: this.asset.identifier || "",
          name: this.asset.name || "",
          categoryName: this.asset.categoryName || "",
          description: this.asset.description || "",
          attributes: [],
        };

        // Load attributes from the new API endpoint
        try {
          // Use sectionId from asset or fallback to current section
          const sectionId = this.asset.sectionId || this.section?.id;

          if (sectionId) {
            const attributes = await this.GetCloneAttributes({
              assetId: this.assetId,
              sectionId: sectionId,
            });

            // Map the API response to match the expected format
            this.cloneData.attributes = Array.isArray(attributes)
              ? attributes.map((attr) => ({
                  ...attr,
                  selected: true,
                  // Use the name field from new API structure, fallback to id for backward compatibility
                  name: attr.name || attr.id,
                  // Use the value field from new API structure, fallback to old structure for backward compatibility
                  value:
                    attr.value ||
                    attr.stringVal ||
                    attr.numVal ||
                    (attr.vals && attr.vals.join(", ")) ||
                    "",
                }))
              : [];
          }
        } catch (error) {
          // Fallback to asset attributes from store if API fails
          this.cloneData.attributes =
            this.asset &&
            this.asset.attributes &&
            Array.isArray(this.asset.attributes)
              ? this.asset.attributes.map((attr) => ({
                  ...attr,
                  selected: true,
                }))
              : [];
        }
      }
    },

    closeDialog() {
      this.isCloning = false;
      this.errorMessage = "";
      this.dialogVisible = false;
    },

    toggleAllAttributes() {
      if (
        this.cloneData.attributes &&
        Array.isArray(this.cloneData.attributes) &&
        this.cloneData.attributes.length > 0
      ) {
        const newValue = !this.allAttributesSelected;
        this.cloneData.attributes.forEach((attr) => {
          this.$set(attr, "selected", newValue);
        });
      }
    },

    getAttributeDisplayValue(attribute) {
      // Prioritize new API response format (value field)
      if (
        attribute.value !== undefined &&
        attribute.value !== null &&
        attribute.value !== ""
      ) {
        return attribute.value.toString();
      }

      // Fallback to old API response format for backward compatibility
      if (attribute.stringVal) {
        return attribute.stringVal;
      }
      if (attribute.numVal !== undefined && attribute.numVal !== null) {
        return attribute.numVal.toString();
      }
      if (attribute.vals && attribute.vals.length > 0) {
        return attribute.vals.join(", ");
      }

      return "";
    },

    async cloneAsset() {
      if (!this.selectedTargetMarket) {
        return;
      }

      if (!this.subid) {
        return;
      }

      // Clear any previous error messages
      this.errorMessage = "";

      // Filter only selected attributes to get their IDs
      const selectedAttributes = (this.cloneData.attributes || []).filter(
        (attr) => attr.selected
      );

      const clonePayload = {
        sourceAssetId: this.assetId,
        targetMarketId: this.selectedTargetMarket,
        userId: this.subid,
        selectedAttributeIds: selectedAttributes.map((attr) => attr.id),
        clonedAssetName: this.cloneData.name,
        clonedAssetDescription: this.cloneData.description,
        clonedAssetId: this.cloneData.identifier,
      };

      try {
        this.isCloning = true;

        // Call the API to clone the asset
        const response = await this.CloneAssetToMarket(clonePayload);

        // Response format: { clonedAssetId: "string", errorMessage: "string" }
        const { clonedAssetId, errorMessage } = response;

        // Check if there's an error message
        if (errorMessage) {
          // Show error message in the dialog and don't close it
          this.errorMessage = errorMessage;
          return;
        }

        // Emit success event to parent component
        this.$emit("clone-success", {
          clonedAssetId,
          targetMarketName: this.selectedTargetMarketName,
          assetName: this.cloneData.name,
        });

        this.closeDialog();
      } catch (error) {
        // Emit error event to parent component
        this.$emit("clone-error", {
          error,
          message: "Failed to clone asset. Please try again.",
        });
      } finally {
        this.isCloning = false;
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/styles/CloneAssetDialog.scss";
</style>
